import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { setAuthToken } from '@/lib/axios'
import { loginWithCredentials, refreshAccessToken } from '@/features/auth/services/authService'
import { TRANSLATION_REGISTRY } from '@/i18n/registry'
import type { Locale } from '@/i18n/types'
import {
  getStoredSession,
  setStoredSession,
  clearStoredSession,
  type StoredSession,
} from './tokenStorage'
import { scheduleRefresh, parseExpiryMs } from './refreshScheduler'
import { PATHS } from '@/lib/routes'
import { useUserStore }    from '@/store/user-store'
import { useBalanceStore } from '@/store/balance-store'

// ---------------------------------------------------------------------------
// Locale-aware auth error messages (no React hook — reads localStorage directly)
// ---------------------------------------------------------------------------

function getAuthMessages() {
  const locale = (localStorage.getItem('locale') as Locale | null) ?? 'es'
  const ns = TRANSLATION_REGISTRY.auth?.[locale] ?? TRANSLATION_REGISTRY.auth?.['es'] ?? {}
  const t = (key: string) => (ns as Record<string, string>)[key] ?? key
  return {
    loginError:     t('auth.error.login'),
    notFound:       t('auth.error.userNotFound'),
    refreshInvalid: t('auth.error.refreshInvalid'),
  }
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type AuthSession = StoredSession

export type Status = 'loading' | 'authenticated' | 'unauthenticated'

export type LoginResult =
  | { status: 'success' }
  | { status: 'error'; message: string }
  | { status: 'password-expired'; token: string }

interface AuthContextValue {
  status:          Status
  session:         AuthSession | null
  signIn:          (email: string, password: string) => Promise<LoginResult>
  signInWithToken: (s: AuthSession) => void
  signOut:         (opts?: { redirectTo?: string }) => void
  refresh:         () => Promise<void>
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status,  setStatus]  = useState<Status>('loading')
  const [session, setSession] = useState<AuthSession | null>(null)

  // navigate is not available at mount time if AuthProvider wraps RouterProvider,
  // but it IS available because AuthProvider is inside RouterProvider's provider chain.
  // To avoid the hook being called outside Router context on first render we use a ref.
  const navigateRef = useRef<ReturnType<typeof useNavigate> | null>(null)
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigateRef.current = useNavigate()
  } catch {
    // AuthProvider is mounted outside a Router (e.g. tests) — ignore
  }

  const cancelRefreshRef = useRef<(() => void) | null>(null)

  // ── Persist session + inject bearer token ─────────────────────────────────

  const applySession = useCallback((s: AuthSession | null) => {
    setSession(s)
    setAuthToken(s?.bearerToken ?? null)

    if (s) {
      setStoredSession(s)
      setStatus('authenticated')
    } else {
      clearStoredSession()
      setStatus('unauthenticated')
      // Clear in-memory stores on every session removal (logout, token expiry,
      // refresh failure) so the next startConnection always sees id === 0 and
      // re-registers the Dashboard handler correctly.
      useUserStore.getState().clearUserData()
      useBalanceStore.getState().clearBalance()
    }
  }, [])

  // ── Refresh ────────────────────────────────────────────────────────────────

  const refresh = useCallback(async () => {
    const current = getStoredSession()
    if (!current) { applySession(null); return }

    try {
      const locale = localStorage.getItem('locale') ?? 'es'
      const msgs = getAuthMessages()
      const { bearerToken, expiration } = await refreshAccessToken(
        current.bearerToken,
        current.refreshToken,
        locale,
        { refreshInvalid: msgs.refreshInvalid, loginError: msgs.loginError },
      )
      // Guard: if logout happened while the API call was in-flight, discard result.
      if (!getStoredSession()) return
      applySession({ ...current, bearerToken, expiration })
      console.info('[Auth] Bearer token refreshed successfully.')
    } catch (error) {
      console.error('[Auth] Token refresh failed:', error)
      applySession(null)
    }
  }, [applySession])

  // ── Schedule refresh whenever session changes ──────────────────────────────

  useEffect(() => {
    cancelRefreshRef.current?.()
    cancelRefreshRef.current = null

    if (!session) return

    cancelRefreshRef.current = scheduleRefresh(session.expiration, refresh)

    return () => {
      cancelRefreshRef.current?.()
      cancelRefreshRef.current = null
    }
  }, [session, refresh])

  // ── Boot — hydrate from localStorage ──────────────────────────────────────

  useEffect(() => {
    const stored = getStoredSession()
    if (!stored) {
      setStatus('unauthenticated')
      return
    }

    const expiryMs = parseExpiryMs(stored.expiration)
    if (isNaN(expiryMs) || expiryMs < Date.now()) {
      // Expired — attempt a refresh
      refresh()
    } else {
      applySession(stored)
    }
  // Run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── signIn ─────────────────────────────────────────────────────────────────

  const signIn = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    try {
      const locale = localStorage.getItem('locale') ?? 'es'
      const msgs = getAuthMessages()
      const user = await loginWithCredentials(email.trim(), password.trim(), locale, {
        notFound:   msgs.notFound,
        loginError: msgs.loginError,
      })

      // Backend-enforced password rotation: don't grant a session, send the
      // caller to ResetPasswordView (same screen used by the "forgot password"
      // email link) with the token it needs to set a new password.
      if (user.requirePasswordChange) {
        return { status: 'password-expired', token: user.refreshToken }
      }

      applySession({
        id:           user.id,
        bearerToken:  user.bearerToken,
        expiration:   user.expiration,
        refreshToken: user.refreshToken,
      })
      return { status: 'success' }
    } catch (error) {
      const message = error instanceof Error ? error.message : getAuthMessages().loginError
      return { status: 'error', message }
    }
  }, [applySession])

  // ── signInWithToken (for switch balance — no password required) ────────────

  const signInWithToken = useCallback((s: AuthSession) => {
    applySession(s)
  }, [applySession])

  // ── signOut ────────────────────────────────────────────────────────────────

  const signOut = useCallback((opts?: { redirectTo?: string }) => {
    cancelRefreshRef.current?.()
    cancelRefreshRef.current = null

    // applySession(null) clears session, axios token, and Zustand stores
    applySession(null)

    localStorage.removeItem('read-notifications')
    sessionStorage.clear()

    const to = opts?.redirectTo ?? PATHS.login
    navigateRef.current?.(to, { replace: true })
  }, [applySession])

  return (
    <AuthContext.Provider value={{ status, session, signIn, signInWithToken, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Internal hook (used by useAuth, RequireAuth, SessionGuard, etc.)
// ---------------------------------------------------------------------------

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuthContext must be used inside <AuthProvider>')
  return ctx
}
